import * as THREE from 'three';
import { CameraModeId, type CameraMode } from '../CameraMode';
import { dampVector3 } from '../CameraDamping';
import type { GolfBall } from '../../simulation/GolfBall';

export type AimDirectionProvider = () => THREE.Vector3;
export type TargetPointProvider = () => THREE.Vector3;
export type GolfBallProvider = GolfBall | (() => GolfBall);

export class BallFollowCameraMode implements CameraMode {
  readonly id = CameraModeId.BallFollow;
  readonly camera = new THREE.PerspectiveCamera(50, 1, 0.1, 4000);
  private aspect = 1;

  constructor(
    private readonly ballProvider: GolfBallProvider,
    private readonly targetPointProvider: THREE.Vector3 | TargetPointProvider,
    private readonly aimDirectionProvider?: AimDirectionProvider,
    private readonly landingZoneProvider?: () => THREE.Vector3 | null,
    private readonly greenReadProvider?: () => boolean,
    private readonly puttingProvider?: () => boolean,
  ) {
    this.camera.position.set(0, 6, -14);
  }

  activate(): void {
    this.update(1 / 60);
  }

  deactivate(): void {}

  update(deltaSeconds: number): void {
    const ball = this.activeBall();
    const ballPosition = ball.state.position;
    const landingZone = this.landingZoneProvider?.() ?? null;
    if (landingZone) {
      this.updateLandingZoneView(ballPosition, landingZone, deltaSeconds);
      return;
    }
    if ((this.greenReadProvider?.() || this.puttingProvider?.()) && ball.state.isResting) {
      this.updateGreenReadView(ballPosition, deltaSeconds);
      return;
    }
    const targetDirection = this.targetDirection(ballPosition);
    const isSetup = ball.state.isResting;

    const isPortrait = this.aspect < 0.8;
    this.camera.fov = isPortrait ? 46 : 50;
    this.camera.updateProjectionMatrix();
    const desiredPosition = ballPosition
      .clone()
      .addScaledVector(targetDirection, isSetup ? (isPortrait ? -16 : -13) : -11)
      .add(new THREE.Vector3(0, isSetup ? (isPortrait ? 5.8 : 4.8) : 4.2, 0));
    const desiredLookAt = ballPosition
      .clone()
      .addScaledVector(targetDirection, isSetup ? (isPortrait ? 34 : 26) : 10)
      .add(new THREE.Vector3(0, isSetup ? 1.35 : 1.4, 0));

    dampVector3(this.camera.position, desiredPosition, isSetup ? 0.16 : 0.22, deltaSeconds);
    const currentLook = new THREE.Vector3();
    this.camera.getWorldDirection(currentLook);
    currentLook.add(this.camera.position);
    dampVector3(currentLook, desiredLookAt, isSetup ? 0.14 : 0.18, deltaSeconds);
    this.camera.lookAt(currentLook);
  }

  private updateLandingZoneView(
    ballPosition: THREE.Vector3,
    landingZone: THREE.Vector3,
    deltaSeconds: number,
  ): void {
    const back = landingZone.clone().sub(ballPosition).setY(0);
    if (back.lengthSq() < 0.001) back.set(0, 0, 1);
    back.normalize();
    const desiredPosition = landingZone
      .clone()
      .addScaledVector(back, -22)
      .add(new THREE.Vector3(0, 32, 0));
    const desiredLookAt = landingZone.clone().add(new THREE.Vector3(0, 0.5, 0));
    dampVector3(this.camera.position, desiredPosition, 0.18, deltaSeconds);
    const currentLook = new THREE.Vector3();
    this.camera.getWorldDirection(currentLook);
    currentLook.add(this.camera.position);
    dampVector3(currentLook, desiredLookAt, 0.2, deltaSeconds);
    this.camera.lookAt(currentLook);
  }

  private updateGreenReadView(ballPosition: THREE.Vector3, deltaSeconds: number): void {
    const targetDirection = this.targetDirection(ballPosition);
    const desiredPosition = ballPosition
      .clone()
      .addScaledVector(targetDirection, -6.2)
      .add(new THREE.Vector3(0, 1.45, 0));
    const desiredLookAt = ballPosition
      .clone()
      .addScaledVector(targetDirection, 9.5)
      .add(new THREE.Vector3(0, 0.16, 0));
    dampVector3(this.camera.position, desiredPosition, 0.2, deltaSeconds);
    const currentLook = new THREE.Vector3();
    this.camera.getWorldDirection(currentLook);
    currentLook.add(this.camera.position);
    dampVector3(currentLook, desiredLookAt, 0.2, deltaSeconds);
    this.camera.lookAt(currentLook);
  }

  handleResize(aspect: number): void {
    this.aspect = aspect;
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {}

  private activeBall(): GolfBall {
    return typeof this.ballProvider === 'function' ? this.ballProvider() : this.ballProvider;
  }

  private targetDirection(ballPosition: THREE.Vector3): THREE.Vector3 {
    if (this.activeBall().state.isResting && this.aimDirectionProvider) {
      const aim = this.aimDirectionProvider().clone().setY(0);
      if (aim.lengthSq() > 0.001) return aim.normalize();
    }

    const targetPoint =
      typeof this.targetPointProvider === 'function'
        ? this.targetPointProvider()
        : this.targetPointProvider;
    const toTarget = targetPoint.clone().sub(ballPosition).setY(0);
    if (toTarget.lengthSq() < 0.001) toTarget.set(0, 0, 1);
    return toTarget.normalize();
  }
}
